<template>
  <li :class="['stage', stage.status]">
    <div class="pool-timeline">
      <div v-if="timeRemaining && stage.status === 'UpNext'" class="countdown">
        {{ timeRemaining }}
      </div>
      <span class="progress-container">
        <v-progress-circular
          :value="stage.status === 'AvailabilityRequested' || isFailed ? 100 : 0"
          :size="30"
          :color="statusColor"
        >
          <v-icon v-if="statusIcon" size="16">{{ statusIcon }}</v-icon>
          <vs-text v-else>{{
            stage.stage_number === 0 ? '' : stage.stage_number
          }}</vs-text></v-progress-circular
        >
      </span>
    </div>
    <div>
      <h5 class="stage-status">{{ statusText }}</h5>
      <pool-stage-availability :candidates="stage.availability" />
    </div>
  </li>
</template>
<script lang="ts" setup>
import { formatDuration } from '@/utils/date-formatting'
import { computed, onUnmounted, ref, watchEffect } from 'vue'
import { Stage } from '@/api/pools'
import moment from 'moment'
import PoolStageAvailability from '@/components/tickets/PoolStageAvailability.vue'

const timeRemaining = ref()
const interval = ref()

const dueBy = computed(() => (props.stage.due_by ? moment.utc(props.stage.due_by) : null))

const props = defineProps<{ stage: Stage }>()
const emit = defineEmits(['expired'])

const isFailed = computed(() => props.stage.availability.every((a) => a.status === 'NotAvailable'))

const statusText = computed(() => {
  if (isFailed.value) {
    return 'Not Available'
  }
  if (props.stage.status === 'AvailabilityRequested') {
    return 'Awaiting Availability'
  }
  if (props.stage.status === 'UpNext') {
    return 'Up Next'
  }

  return props.stage.status
})

const statusColor = computed(() => {
  if (isFailed.value) {
    return 'var(--color-red--light)'
  }
  if (props.stage.status === 'AvailabilityRequested') {
    return 'var(--color-green)'
  }

  return 'var(--color-blue)'
})

const statusIcon = computed(() => {
  if (isFailed.value) {
    return 'mdi-close'
  }
  if (props.stage.status === 'AvailabilityRequested') {
    return 'mdi-email-arrow-right-outline'
  }
  if (props.stage.status === 'Queued') {
    return 'mdi-clock-outline'
  }

  return null
})

watchEffect(() => {
  if (!props.stage.due_by) {
    return
  }
  if (interval.value) {
    clearInterval(interval.value)
  }
  interval.value = setInterval(() => {
    if (!dueBy.value) {
      return
    }
    if (props.stage.status !== 'UpNext') {
      timeRemaining.value = null
      clearInterval(interval.value)
      return
    }
    const duration = moment.duration(dueBy.value.diff(moment.utc()))
    if (duration.asMilliseconds() <= 0) {
      emit('expired')
      clearInterval(interval.value)
      timeRemaining.value = null
      return
    }

    timeRemaining.value = formatDuration(duration)
  }, 1000)
})

onUnmounted(() => {
  clearInterval(interval.value)
})
</script>

<style scoped>
.stage.Queued {
  opacity: 0.6;
}

.stage {
  display: flex;
  align-items: center;
  min-height: 3rem;
}

.pool-timeline {
  position: relative;
  display: flex;
  flex-direction: column;
  margin-right: 2rem;
}

.countdown {
  position: absolute;
  top: -15px;
  width: 100%;
  font-size: 0.55em;
  text-align: center;
  white-space: nowrap;
}
</style>
